import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, IconButton, Box, CircularProgress, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { JITSI_SERVER_URL } from '../../config';

// Jitsi's browser API is loaded from the meet server itself rather than npm, so it always
// matches the deployed server version. Memoised so reopening the dialog does not re-inject
// the script tag.
let externalApiPromise = null;

const loadExternalApi = () => {
    if (window.JitsiMeetExternalAPI) return Promise.resolve();
    if (externalApiPromise) return externalApiPromise;

    externalApiPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = `${JITSI_SERVER_URL}/external_api.js`;
        script.async = true;
        script.onload = resolve;
        script.onerror = () => {
            // Let a later attempt retry instead of caching the failure forever.
            externalApiPromise = null;
            reject(new Error('Failed to load the video call library'));
        };
        document.head.appendChild(script);
    });

    return externalApiPromise;
};

/**
 * Full-screen embedded Jitsi call.
 *
 * @param {boolean} open
 * @param {string}  roomName  HMAC-derived room from /ability_hub/videocall/room
 * @param {string}  subject   Displayed as the conference subject (the client's name)
 * @param {object}  user      Logged-in FW user, for the Jitsi display name/email
 * @param {func}    onClose
 * @param {func}    onCallEnded  ({startedAt, endedAt, viaBeacon}) once the call is over, so
 *                               the caller can log it as a task. Never fires if the worker
 *                               opened the dialog but never joined the conference.
 */
const JitsiCallDialog = ({ open, roomName, subject, user, onClose, onCallEnded }) => {
    const containerRef = useRef(null);
    const apiRef = useRef(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    // Set when the conference is actually joined -- not when the dialog mounts -- so the
    // logged duration excludes time spent on the prejoin screen picking a camera.
    const startedAtRef = useRef(null);
    // A single call can signal its end more than once (videoConferenceLeft, then
    // readyToClose, then effect teardown). Only the first report should count.
    const reportedRef = useRef(false);

    // Callers pass an inline arrow for onClose; keeping it in the effect deps would tear
    // down and rebuild the live call on every parent render.
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    const onCallEndedRef = useRef(onCallEnded);
    onCallEndedRef.current = onCallEnded;

    const reportCallEnded = useCallback((viaBeacon = false) => {
        if (reportedRef.current || !startedAtRef.current) return;
        reportedRef.current = true;

        onCallEndedRef.current?.({
            startedAt: startedAtRef.current,
            endedAt: new Date().toISOString(),
            viaBeacon
        });
    }, []);

    useEffect(() => {
        if (!open || !roomName) return undefined;

        let cancelled = false;
        setError(null);
        setLoading(true);
        startedAtRef.current = null;
        reportedRef.current = false;

        // A tab closed mid-call never reaches the teardown below, so flush synchronously.
        const handleBeforeUnload = () => reportCallEnded(true);
        window.addEventListener('beforeunload', handleBeforeUnload);

        loadExternalApi().then(() => {
            // The dialog may have closed while the script was still loading.
            if (cancelled || !containerRef.current) return;

            const domain = JITSI_SERVER_URL.replace(/^https?:\/\//, '');

            apiRef.current = new window.JitsiMeetExternalAPI(domain, {
                roomName,
                parentNode: containerRef.current,
                userInfo: {
                    displayName: user?.emp_name ?? '',
                    email: user?.email ?? ''
                },
                configOverwrite: {
                    subject: subject ?? '',
                    hideConferenceSubject: !subject,
                    prejoinPageEnabled: true
                },
                interfaceConfigOverwrite: {
                    // Mirrors the flags the Ability Hub mobile app disables.
                    TOOLBAR_BUTTONS: [
                        'microphone', 'camera', 'desktop', 'fullscreen', 'fodeviceselection',
                        'hangup', 'chat', 'settings', 'raisehand', 'videoquality', 'filmstrip',
                        'tileview', 'select-background'
                    ],
                    SHOW_JITSI_WATERMARK: false,
                    SHOW_BRAND_WATERMARK: false
                }
            });

            apiRef.current.addEventListener('videoConferenceJoined', () => {
                startedAtRef.current = new Date().toISOString();
            });
            apiRef.current.addEventListener('videoConferenceLeft', () => reportCallEnded());
            apiRef.current.addEventListener('readyToClose', () => {
                reportCallEnded();
                onCloseRef.current();
            });
            setLoading(false);
        }).catch((err) => {
            if (!cancelled) setError(err.message);
        });

        return () => {
            cancelled = true;
            window.removeEventListener('beforeunload', handleBeforeUnload);
            // Covers closing the dialog with the X while still in the conference, where
            // Jitsi is disposed before it can emit videoConferenceLeft.
            reportCallEnded();
            // Without dispose() the call keeps running -- camera and mic stay live -- after
            // the dialog unmounts.
            if (apiRef.current) {
                apiRef.current.dispose();
                apiRef.current = null;
            }
        };
        // subject/user are read once at construction; re-running on their identity would
        // needlessly restart the call.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, roomName]);

    const overlay = (message) => (
        <Box sx={{
            position: 'absolute', inset: 0, display: 'flex', gap: 2,
            alignItems: 'center', justifyContent: 'center', flexDirection: 'column'
        }}>
            {message ? <Typography sx={{ color: '#fff' }}>{message}</Typography> : <CircularProgress />}
        </Box>
    );

    // App.js portals a fixed "Send email to support" button to document.body at zIndex 2000,
    // deliberately above MUI's default modal layer (1300) so it stays reachable from any
    // dialog. A full-screen call should own the whole viewport, so this dialog sits above it.
    return (
        <Dialog open={open} onClose={onClose} fullScreen sx={{ zIndex: 2100 }}>
            <Box sx={{ position: 'relative', height: '100%', bgcolor: '#040404' }}>
                <IconButton
                    onClick={onClose}
                    sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, color: '#fff' }}
                >
                    <CloseIcon />
                </IconButton>

                {/* Jitsi appends its iframe here, so the container must stay mounted and empty. */}
                <Box ref={containerRef} sx={{ height: '100%', width: '100%' }} />

                {error && overlay(error)}
                {loading && !error && overlay(null)}
            </Box>
        </Dialog>
    );
};

export default JitsiCallDialog;
